import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  SubmitButton,
  useSubmittingMutation,
  useToast,
  AutosaveIndicator,
} from "@/design-system/components/ui";
import { parseFormSchema } from "@/lib/formSchema";
import {
  PortalFormRenderer,
  findFirstMissingRequired,
  findFirstFormatError,
  type PortalFormData,
} from "../components/PortalFormRenderer";
import { WaitState } from "../components/WaitState";
import { SubmissionStatusCard } from "../components/SubmissionStatusCard";
import { portalPackagesApi } from "../api/packages";
import type { CollectionAiFeedbackResult, PortalItemDto } from "../api/types";
import { InHouseSigningSurface } from "./InHouseSigningSurface";
import { ApprovalChainItemSurface } from "./ApprovalChainItemSurface";
import { useLetterPreview } from "../hooks/useLetterPreview";
import { ResponseBlockedHint, type PortalResponse } from "../components/ResponseStep";
import { SIGNATURE_STEP_NOT_SET_UP, signingRouteFor } from "../lib/signingRoute";

// External Intake & Collection v1.0 PR 4 §2.3 — Form fulfilment surface.
// Renders the template's FormSchemaJson with the portal form renderer.
// On every edit the recipient makes, debounces an autosave to PUT
// /items/:id/form-data — the first save bumps Pending → InProgress
// server-side. Opening the item never writes (A16): nothing is saved until
// the recipient actually changes an answer, so merely viewing a returned or
// letter item can't move its status or re-serialise its saved data. On
// Submit, validates required fields client-side, runs the AI review
// checkpoint when the template has feedback enabled, and POSTs
// /items/:id/submit-form on confirm.
//
// UAT #15 — pre-submit AI review checkpoint. For both plain-submit and
// signature-required forms, the recipient sees an advisory pass before
// the binding action (submit / continue-to-sign). Feedback is advisory:
// the recipient may continue past warnings or revise; an unavailable /
// failed call never blocks.

const AUTOSAVE_DELAY_MS = 900;

interface FormItemSurfaceProps {
  item: PortalItemDto;
  onUpdated: (next: PortalItemDto) => void;
  disabled?: boolean;
  /** Review loop — set while the item owes a response; the note is sent with
   *  the final action (submit, or the in-house signature). */
  response?: PortalResponse | null;
  /** Reports the in-house signature POST being in flight (ItemPage locks
   *  navigation meanwhile). */
  onSigningBusyChange?: (busy: boolean) => void;
  /** Approval chains — re-read the item after the caller's step lands. */
  onReload?: () => void | Promise<void>;
}

export function FormItemSurface({
  item,
  onUpdated,
  disabled,
  response,
  onSigningBusyChange,
  onReload,
}: FormItemSurfaceProps) {
  const toast = useToast();
  const schema = useMemo(() => parseFormSchema(item.formSchemaJson), [item.formSchemaJson]);
  const [data, setData] = useState<PortalFormData>(() => {
    if (!item.savedFormDataJson) return {};
    try {
      const parsed = JSON.parse(item.savedFormDataJson) as unknown;
      return parsed && typeof parsed === "object" ? (parsed as PortalFormData) : {};
    } catch {
      return {};
    }
  });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // PR 8 §9 — Form items can require a signature. When the server-enforced
  // route is in-house, "Submit" is replaced by "Continue to sign" → the
  // in-house signing surface runs against the same item, which renders the
  // captured form data + signature to a flattened PDF on submit. A DocuSign
  // route can't be fulfilled from a Form item and is shown blocked (A18).
  const signingRoute = signingRouteFor(item);
  const requiresInHouseSignature = signingRoute === "InHouse";
  const [signingPhase, setSigningPhase] = useState(false);
  const [feedback, setFeedback] = useState<CollectionAiFeedbackResult | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string>(item.savedFormDataJson ?? "");
  // A16 — set only by a real edit from the recipient. Until then nothing is
  // saved: the server's own serialisation of savedFormDataJson (e.g.
  // `500000.00`) never round-trips byte-for-byte through JSON.parse/stringify,
  // and an empty item stringifies to "{}", so comparing blobs alone would
  // write on open.
  const dirtyRef = useRef(false);
  const handleChange = useCallback((next: PortalFormData) => {
    dirtyRef.current = true;
    setData(next);
  }, []);

  const flushSave = useCallback(
    async (payload: PortalFormData) => {
      const blob = JSON.stringify(payload);
      if (blob === lastSentRef.current) return;
      setSaveState("saving");
      try {
        const next = await portalPackagesApi.saveFormData(item.id, blob);
        lastSentRef.current = blob;
        setLastSavedAt(new Date().toISOString());
        setSaveState("saved");
        onUpdated(next);
      } catch (err) {
        setSaveState("error");
        setErrorMsg(err instanceof Error ? err.message : "Couldn't save your answers.");
      }
    },
    [item.id, onUpdated],
  );

  useEffect(() => {
    if (disabled || !dirtyRef.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void flushSave(data);
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [data, disabled, flushSave]);

  const review = useSubmittingMutation(async () => {
    // Persist any in-flight typing before the backend reads
    // SavedFormDataJson for the LLM prompt. Untouched answers are already
    // stored as-is — re-saving them would be a write the recipient didn't make.
    if (dirtyRef.current) await flushSave(data);
    try {
      const result = await portalPackagesApi.previewFormFeedback(item.id);
      setFeedback(result);
    } catch {
      // Advisory only — degrade to "unavailable" so the recipient can
      // still continue rather than getting stuck on a slow / failed AI.
      setFeedback({
        verdict: "Unavailable",
        summary: "We couldn't preview this right now — you can still submit.",
        issues: [],
        available: false,
      });
    }
  });

  const confirm = useSubmittingMutation(
    async () => {
      if (requiresInHouseSignature) {
        setSigningPhase(true);
        return;
      }
      const next = await portalPackagesApi.submitForm(item.id, response?.note);
      onUpdated(next);
      toast.success("Submitted ✓ — your answers are now with your liaison for review.");
    },
    {
      onError: (err) =>
        setErrorMsg(err instanceof Error ? err.message : "Could not submit. Try again in a moment."),
    },
  );

  const busy = review.pending || confirm.pending;

  function handleReview() {
    if (!schema) return;
    const missing = findFirstMissingRequired(schema, data);
    if (missing) {
      setErrorMsg(`Please fill in "${missing.label}" before submitting.`);
      return;
    }
    const formatError = findFirstFormatError(schema, data);
    if (formatError) {
      setErrorMsg(`"${formatError.field.label}" — ${formatError.message}`);
      return;
    }
    setErrorMsg(null);
    void review.submit();
  }

  function handleConfirm() {
    setErrorMsg(null);
    void confirm.submit();
  }

  // Approval chains — a chain item is a document to endorse or sign in turn,
  // driven only by its chain view; none of the form / review-loop UI below
  // applies (its body may be an uploaded PDF with no schema at all).
  if (item.approvalChain) {
    return (
      <ApprovalChainItemSurface
        item={item}
        onUpdated={onUpdated}
        onReload={onReload}
        onSigningBusyChange={onSigningBusyChange}
      />
    );
  }

  if (!schema) {
    return (
      <p className="text-small text-status-error m-0" role="alert">
        This form is missing its schema. Please contact us so we can fix it.
      </p>
    );
  }

  if (signingRoute === "DocuSign" && !disabled) {
    return (
      <p className="text-small text-status-error m-0" role="alert" data-testid="signature-step-blocked">
        {SIGNATURE_STEP_NOT_SET_UP}
      </p>
    );
  }

  if (signingPhase) {
    return (
      <InHouseSigningSurface
        item={item}
        onUpdated={onUpdated}
        response={response}
        onBusyChange={onSigningBusyChange}
      />
    );
  }

  // Sender-Fields v1 — a letter item (template carries a body) shows the
  // actual letter PDF for review (Download/Print), NOT the read-only sender
  // field grid, and skips the AI review checkpoint / "Edit answers" entirely
  // (every field is sender-filled; there's nothing for the recipient to edit
  // or for AI to review). They review now and sign whenever ready.
  if (item.hasLetterBody) {
    return (
      <LetterReviewSurface
        item={item}
        disabled={disabled}
        onContinueToSign={() => setSigningPhase(true)}
      />
    );
  }

  const confirmLabel = requiresInHouseSignature ? "Continue to sign" : "Submit for review";
  const reviewLabel = requiresInHouseSignature
    ? "Review before signing"
    : "Review before submitting";

  return (
    <div className="flex flex-col gap-space-4">
      {!disabled && item.status === "InProgress" && !feedback && !busy && (
        <SubmissionStatusCard tone="progress" title="In progress" data-testid="form-in-progress">
          You've started this request — your answers save automatically as you go. Submit it for
          review when you're ready.
        </SubmissionStatusCard>
      )}
      {/* Leads-style framed form card so the portal form reads the same as
          the staff Lead edit form. */}
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
        <PortalFormRenderer
          schema={schema}
          value={data}
          onChange={handleChange}
          disabled={disabled || busy || feedback !== null}
        />
      </div>

      {review.pending && (
        <WaitState
          label="Checking your answers…"
          message="Our assistant is reviewing what you entered. This can take up to a minute — please don't close this tab."
        />
      )}

      {feedback && <FormFeedbackPanel feedback={feedback} />}

      {!disabled && (
        <div className="flex items-center justify-between gap-space-3 flex-wrap">
          <AutosaveIndicator
            state={saveState}
            lastSavedAt={lastSavedAt}
            idleHint={
              item.savedFormDataJson
                ? "Your last answers are restored."
                : "Your answers save automatically."
            }
            data-testid="autosave-indicator"
          />
          {feedback ? (
            <div className="flex items-center gap-space-2 flex-wrap">
              <Button
                variant="secondary"
                onClick={() => setFeedback(null)}
                disabled={busy}
              >
                Edit answers
              </Button>
              <SubmitButton
                onClick={handleConfirm}
                pending={confirm.pending}
                pendingLabel="Submitting…"
                disabled={busy || (!requiresInHouseSignature && !!response?.blockedReason)}
                data-testid="form-confirm"
              >
                {confirmLabel}
              </SubmitButton>
            </div>
          ) : (
            <SubmitButton
              onClick={handleReview}
              pending={review.pending}
              pendingLabel="Checking…"
              disabled={busy}
            >
              {reviewLabel}
            </SubmitButton>
          )}
        </div>
      )}

      {!disabled && feedback && !requiresInHouseSignature && (
        <ResponseBlockedHint response={response} />
      )}

      {confirm.pending && (
        <SubmissionStatusCard tone="progress" title="Submitting…" data-testid="form-submitting">
          We're sending your answers for review. This can take a moment — please don't close this
          tab.
        </SubmissionStatusCard>
      )}

      {disabled && item.status === "SubmittedForReview" && (
        <SubmissionStatusCard tone="review" title="Submitted for review" data-testid="form-under-review">
          Your answers are under review. We'll let you know if anything is needed.
        </SubmissionStatusCard>
      )}

      {errorMsg && <p className="text-small text-status-error m-0" role="alert">{errorMsg}</p>}
    </div>
  );
}

function LetterReviewSurface({
  item,
  disabled,
  onContinueToSign,
}: {
  item: PortalItemDto;
  disabled?: boolean;
  onContinueToSign: () => void;
}) {
  const { url, error, loading } = useLetterPreview(item.id, true);
  const canSign =
    !disabled
    && (item.status === "Pending"
      || item.status === "InProgress"
      || item.status === "ChangesRequested");

  return (
    <div className="flex flex-col gap-space-4">
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4 flex flex-col gap-space-3">
        <div className="flex items-center justify-between gap-space-2 flex-wrap">
          <h2 className="text-h4 font-display text-onyx m-0">Your letter</h2>
          {url && (
            <div className="flex items-center gap-space-3">
              <a
                href={url}
                download={`${item.templateName || "letter"}.pdf`}
                className="text-caption text-gold underline"
              >
                Download
              </a>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-caption text-gold underline"
              >
                Open / print
              </a>
            </div>
          )}
        </div>
        <p className="text-small text-gray-mid m-0">
          Please review your letter below. You can download or print a copy for your records, and
          sign whenever you're ready — there's no rush.
        </p>
        {error ? (
          <p className="text-small text-status-error m-0" role="alert">
            We couldn't load the document right now. Please refresh and try again.
          </p>
        ) : url ? (
          <iframe
            src={url}
            title={`Letter — ${item.templateName}`}
            className="w-full rounded-md border border-border-light bg-white-warm"
            style={{ height: "min(720px, 75dvh)" }}
          />
        ) : (
          <p className="text-small text-gray-mid italic m-0">
            {loading ? "Loading your letter…" : "Preparing your letter…"}
          </p>
        )}
      </div>

      {canSign ? (
        <div className="flex justify-end">
          <Button onClick={onContinueToSign}>Continue to sign</Button>
        </div>
      ) : disabled && item.status === "SubmittedForReview" ? (
        <p className="text-small text-gray-mid m-0">
          Thanks for signing. Your letter is being reviewed.
        </p>
      ) : null}
    </div>
  );
}

function FormFeedbackPanel({ feedback }: { feedback: CollectionAiFeedbackResult }) {
  const tone =
    feedback.verdict === "LooksGood" ? "bg-status-success/10 border-status-success" :
    feedback.verdict === "NeedsAttention" ? "bg-status-warning/10 border-status-warning" :
    "bg-white-pure border-border-light";

  const headline =
    feedback.verdict === "LooksGood" ? "Looks good!" :
    feedback.verdict === "NeedsAttention" ? "A couple of things to check" :
    "We couldn't preview this — submit when you're ready";

  return (
    <div className={`p-space-3 border rounded-md ${tone}`} data-testid="form-feedback">
      <AssistantHeader />
      <p className="text-small font-medium text-onyx m-0 mt-space-2">{headline}</p>
      {feedback.summary && (
        <p className="text-small text-onyx m-0 mt-space-2">{feedback.summary}</p>
      )}
      {feedback.issues.length > 0 && (
        <ul className="text-small text-onyx m-0 mt-space-2 pl-space-4 list-disc">
          {feedback.issues.map((issue, idx) => (
            <li key={idx} className="mt-space-1">
              <strong>{issue.title}</strong>
              {issue.suggestion ? ` — ${issue.suggestion}` : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AssistantHeader() {
  return (
    <div className="flex items-center gap-space-2" data-testid="assistant-header">
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold/30 text-onyx text-caption font-display font-bold"
      >
        P
      </span>
      <span className="text-caption text-gray-mid uppercase tracking-wide">
        Phoenix Assistant
      </span>
    </div>
  );
}
